import { Component, Input, OnInit } from '@angular/core';
import { AlertController, ModalController } from '@ionic/angular';
import { ApiService } from 'src/app/api/api.service';
import { GlobalService } from 'src/app/api/global.service';
import { UploadComponent } from '../../files/upload/upload.component';
import { ExplorerComponent } from '../../files/explorer/explorer.component';
import { EmailTypes } from '../../../secure-email/EmailTypes';

// Shown right after a "start action" email-backed step (secure email, document/approval/payment
// request, survey, appointment/question request - anything but a signature request) creates its
// secure email. The team member has just added files via "add task files to email" or per-file
// selection on the step itself, but has no easy way to see what actually landed on the email or to
// add more before the client sees it - this closes that gap without opening the full email details
// page (resend/reminders/etc. would be noise here; this is just a quick confirm + attach step).
@Component({
  selector: 'app-email-files-prompt',
  templateUrl: './email-files-prompt.component.html',
  styleUrls: ['./email-files-prompt.component.scss'],
})
export class EmailFilesPromptComponent implements OnInit {
  @Input() email_guid: string;
  @Input() step_title = '';

  email: any = null;
  files: any[] = [];
  loading = true;
  signature_file_guids: string[] = [];

  constructor(private modalController: ModalController, private alertController: AlertController, private apiService: ApiService, public globalService: GlobalService) { }

  ngOnInit() {
    void this.GetData();
  }

  async GetData() {
    this.loading = true;
    const email_data: any = await this.apiService.get(`emails/${this.email_guid}`);
    this.email = email_data?.success && email_data.data?.length > 0 ? email_data.data[0] : null;

    const files_data: any = await this.apiService.get(`emails/files/${this.email_guid}`);
    this.files = files_data?.success ? files_data.data : [];

    if (this.email?.type == EmailTypes.signature_request) {
      const sig_data: any = await this.apiService.get(`emails/${this.email_guid}/signature_files`);
      this.signature_file_guids = sig_data?.success ? (sig_data.data || []).map((f: any) => f.file_guid) : [];
    }

    this.loading = false;
  }

  // Governs the remove (x) next to each file: only files the accountant themselves attached (not a
  // client upload) can go, only while the client hasn't opened the email yet (status 0/Unopened - it
  // locks the moment that changes, since they may already be relying on what they saw), and never the
  // specific document a signature_request is asking to be signed.
  CanRemoveFile(file: any): boolean {
    if (this.email?.status != 0) return false;
    if (file.email_files?.[0]?.outside_upload) return false;
    if (this.email?.type == EmailTypes.signature_request && this.signature_file_guids.includes(file.guid)) return false;
    return true;
  }

  async RemoveFile(file: any) {
    const alert = await this.alertController.create({
      header: 'Remove File',
      message: `Are you sure you want to remove "${file.file_name}" from this email?`,
      buttons: [
        { text: 'Cancel', role: 'cancel' },
        {
          text: 'Remove',
          handler: async () => {
            const data: any = await this.apiService.delete(`emails/${this.email_guid}/files/${file.guid}`, null);
            if (data?.success) await this.GetData();
            else this.globalService.presentToastMessage(data?.message || 'Failed to remove file', false);
          }
        }
      ]
    });
    await alert.present();
  }

  // Mirrors DetailsPage.GetLinkedRootFolderGuid in secure-email/details: picks a sensible starting
  // folder for the file explorer picker (the email's step's project, else wherever an already
  // attached file lives, else the linked company/client's root, else Home).
  async GetLinkedRootFolderGuid(): Promise<string> {
    const email: any = this.email;

    if (email?.step_project_guid) {
      const project_data: any = await this.apiService.get(`projects/${email.step_project_guid}`);
      const project = project_data?.success && project_data.data && project_data.data[0];
      if (project?.folder_guid) return project.folder_guid;
    }

    if (this.files.length > 0) {
      const infos: any[] = await Promise.all(this.files.map((f: any) => this.apiService.get(`files/${f.guid}/info`)));
      const file_infos = infos
        .map((d: any) => (d?.success && d.data && d.data[0]) ? d.data[0] : null)
        .filter(Boolean);

      const with_project = file_infos.find((f: any) => f.project_guid);
      if (with_project) {
        const project_data: any = await this.apiService.get(`projects/${with_project.project_guid}`);
        const project = project_data?.success && project_data.data && project_data.data[0];
        if (project?.folder_guid) return project.folder_guid;
      }
    }

    const company: any = email?.company;
    if (company?.folder?.[0]?.guid) return company.folder[0].guid;
    const client: any = email?.client;
    if (client?.folder?.[0]?.guid) return client.folder[0].guid;

    return '0';
  }

  async UploadFile() {
    const modal = await this.modalController.create({
      component: UploadComponent,
      cssClass: 'small-modal secondary-modal',
      componentProps: {
        folder_guid: '0',
        additional_data: { email_guid: this.email_guid },
        title: 'Add Files to Email'
      },
    });
    modal.present();
    const { data } = await modal.onDidDismiss();
    if (data && data.length > 0) await this.GetData();
  }

  async SelectExistingFile() {
    const root_folder_guid = await this.GetLinkedRootFolderGuid();
    const company: any = this.email?.company;
    const client: any = this.email?.client;
    const modal = await this.modalController.create({
      component: ExplorerComponent,
      componentProps: {
        file_select_mode: true,
        root_folder_guid,
        linked_client_guid: client?.guid || null,
        linked_company_guid: company?.guid || null,
        linked_entity_name: company?.name || client?.display_name || null,
        // Picking a file here attaches it to an email the client will receive - an Internal Only
        // file shouldn't be pickable to send to them in the first place.
        show_hidden_to_client: false
      },
      backdropDismiss: true,
      cssClass: 'large-modal'
    });
    modal.present();
    const { data } = await modal.onWillDismiss();
    if (data && data.length > 0) {
      await this.globalService.presentLoading('Attaching files...', 30000);
      const file_list = data.map((f: any) => ({ guid: f.guid, file_name: f.file_name, was_selected: true }));
      await this.apiService.put(`emails/${this.email_guid}`, { file_list });
      await this.globalService.dismissLoading();
      await this.GetData();
    }
  }

  Done() {
    this.modalController.dismiss();
  }
}
